import { CheckCircle2 } from "lucide-react";
import type { FC } from "react";

import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";

import { useClientPortal } from "../components/ClientPortalShell";
import {
  CaseSummary,
  ComingUpCard,
  PortalCard,
  ReminderCards,
  TaskRows,
  TeamList
} from "../components/PortalParts";
import { usePortalT } from "../i18n";
import { greetingKey, isTaskReady, sortTasks } from "../portalModel";
import { useClientPortalHome } from "../queries";
import type { PortalHome } from "../types";

const subtitle = (
  home: PortalHome,
  t: ReturnType<typeof usePortalT>["t"]
): string => {
  const [first] = home.cases;
  const parts: string[] = [];
  if (first && home.cases.length === 1)
    parts.push(t(`greeting.stageLine.${first.stage}`));
  const ready = home.tasks.filter(isTaskReady).length;
  parts.push(
    ready
      ? t("greeting.withTasks", { count: ready })
      : t("greeting.allCaughtUp")
  );
  return parts.join(" ");
};

export const PortalLoadError: FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = usePortalT();
  return (
    <Empty className="border border-border bg-background">
      <EmptyHeader>
        <EmptyDescription>{t("shell.loadFailed")}</EmptyDescription>
      </EmptyHeader>
      <Button variant="outline" onClick={onRetry}>
        {t("shell.retry")}
      </Button>
    </Empty>
  );
};

export const PortalHomePage: FC = () => {
  const me = useClientPortal();
  const { t } = usePortalT();
  const home = useClientPortalHome();

  if (home.isLoading) return <LoadingState size="lg" />;
  if (!home.data)
    return <PortalLoadError onRetry={() => void home.refetch()} />;

  const { tasks, cases, reminders, team, appointments } = home.data;
  const sorted = sortTasks(tasks);
  const readyCount = tasks.filter(isTaskReady).length;
  return (
    <>
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t(greetingKey(new Date().getHours()), {
            name: me.client.first_name
          })}
        </h1>
        <p className="text-md text-muted-foreground">
          {subtitle(home.data, t)}
        </p>
      </header>
      <div className="grid items-start gap-5 lg:grid-cols-[1.75fr_1fr]">
        <div className="flex flex-col gap-5">
          <PortalCard
            title={t("tasks.title")}
            action={
              readyCount ? (
                <span className="text-sm text-muted-foreground">
                  {t("tasks.count", { count: readyCount })}
                </span>
              ) : null
            }
          >
            {sorted.length ? (
              <TaskRows tasks={sorted} preview={me.preview} />
            ) : (
              <Empty className="p-6">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CheckCircle2 />
                  </EmptyMedia>
                  <EmptyTitle>{t("tasks.none")}</EmptyTitle>
                  <EmptyDescription>{t("tasks.noneBody")}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </PortalCard>
          {cases.length ? (
            <PortalCard
              title={cases.length > 1 ? t("case.titlePlural") : t("case.title")}
            >
              <div className="flex flex-col gap-6">
                {cases.map((c) => (
                  <CaseSummary key={c.id} portalCase={c} link />
                ))}
              </div>
            </PortalCard>
          ) : null}
        </div>
        <div className="flex flex-col gap-5">
          {team.length ? (
            <PortalCard title={t("team.title")}>
              <TeamList team={team} />
            </PortalCard>
          ) : null}
          <ComingUpCard appointments={appointments ?? []} tasks={tasks} />
          <ReminderCards reminders={reminders} />
          <p className="px-1 text-xs text-muted-foreground">
            {t("shell.privacy")}
          </p>
        </div>
      </div>
    </>
  );
};
